import { createPortal } from 'react-dom';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { IconCheck, IconChevronRight, IconClock, IconX } from '@tabler/icons-react';
import {
  Button,
  ProximityScope,
  ConfirmModalFooter,
  IconButton,
  Modal,
  ScrollArea,
  Text,
  useOverlayFocusTrap,
} from '@wapharma/design-system';
import { useGuide } from '../contexts/GuideContext';
import { collapseSetupWizardPanel } from '../lib/setupShell';
import { TOURS_ENABLED } from '../tours/flags';
import dockStyles from './OnboardingDock.module.css';
import setupStyles from './SetupWizard.module.css';
import { StepTravel } from './StepTravel';

const COMPANION_HEIGHT_VAR = '--wp-companion-dock-height';
const COMPANION_PAD_VAR = '--wp-companion-dock-pad';

function publishDockMetrics(sheet: HTMLElement): void {
  const fullH = Math.ceil(sheet.getBoundingClientRect().height);
  const strip = sheet.querySelector('[data-wp-dock-strip]');
  const stripH = strip
    ? Math.ceil(strip.getBoundingClientRect().height)
    : fullH;
  document.documentElement.style.setProperty(COMPANION_HEIGHT_VAR, `${fullH}px`);
  document.documentElement.style.setProperty(COMPANION_PAD_VAR, `${stripH}px`);
}

/**
 * Bottom companion drawer — same step chrome as Configuration initiale
 * (numbered pack tabs, one step body, Retour / Continuer).
 */
export function OnboardingDock() {
  const guide = useGuide();
  const titleId = useId();
  const sheetRef = useRef<HTMLElement | null>(null);
  const expanded = guide.dockOpen;
  const stripNudge = guide.parcoursStripNudge && !expanded;
  const visible = guide.showCompanion || expanded || stripNudge;
  useOverlayFocusTrap(visible && expanded, sheetRef);
  const [dismissConfirmOpen, setDismissConfirmOpen] = useState(false);

  const groups = guide.checklistGroups;
  const stepIndex = useMemo(() => {
    const id = guide.activePackId;
    if (!id) return 0;
    const i = groups.findIndex((g) => g.entryId === id);
    return i >= 0 ? i : 0;
  }, [groups, guide.activePackId]);

  const currentGroup = groups[stepIndex] ?? null;
  const onLastStep = stepIndex >= groups.length - 1;
  const headerStepLabel = currentGroup?.title ?? 'Parcours';

  useEffect(() => {
    if (expanded) collapseSetupWizardPanel();
  }, [expanded]);

  useEffect(() => {
    if (!visible) {
      document.documentElement.style.removeProperty(COMPANION_HEIGHT_VAR);
      document.documentElement.style.removeProperty(COMPANION_PAD_VAR);
      return;
    }
    const el = sheetRef.current;
    if (!el) return;
    const publish = () => publishDockMetrics(el);
    publish();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(publish) : null;
    ro?.observe(el);
    return () => {
      ro?.disconnect();
      document.documentElement.style.removeProperty(COMPANION_HEIGHT_VAR);
      document.documentElement.style.removeProperty(COMPANION_PAD_VAR);
    };
  }, [visible, expanded, stepIndex, currentGroup?.rows.length]);

  useEffect(() => {
    if (!expanded) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      e.preventDefault();
      guide.closeDock();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [expanded, guide.closeDock]);

  useEffect(() => {
    if (!stripNudge) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      e.preventDefault();
      guide.clearParcoursStripNudge();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [stripNudge, guide.clearParcoursStripNudge]);

  if (!TOURS_ENABLED || !visible) return null;

  const pct = guide.progressPercent;
  const progressLabel = `${pct}\u00a0%`;
  const headerAria = `Parcours, ${progressLabel}, étape ${headerStepLabel}`;

  const goToStep = (i: number) => {
    const g = groups[i];
    if (!g) return;
    if (guide.isPackBlocked(g.entryId)) return;
    if (i > stepIndex) {
      const priorsDone = groups
        .slice(0, i)
        .every(
          (pg) => pg.rows.length > 0 && pg.rows.every((r) => r.packReady),
        );
      if (!priorsDone) return;
    }
    guide.selectPack(g.entryId);
  };

  const currentPackToursDone = Boolean(
    currentGroup &&
      currentGroup.rows.length > 0 &&
      currentGroup.rows.every((r) => r.packReady),
  );

  const sheet = (
    <div className={dockStyles.portalHost} data-wp-onboarding-dock="">
      <div
        className={
          expanded || stripNudge
            ? `${dockStyles.backdrop} ${dockStyles.backdropVisible}`
            : dockStyles.backdrop
        }
        aria-hidden={!stripNudge}
        onMouseDown={(e) => {
          if (e.target !== e.currentTarget) return;
          if (stripNudge) {
            guide.clearParcoursStripNudge();
            return;
          }
          if (expanded) guide.closeDock();
        }}
      />
      {stripNudge ? (
        <div className={dockStyles.stripNudgeTip} role="status">
          <Text size="sm" fw={600}>
            Parcours métier
          </Text>
          <Text size="xs" c="dimmed">
            Ouvrez ce bandeau pour démarrer le guide.
          </Text>
          <Button
            type="button"
            size="sm"
            variant="primary"
            onClick={() => guide.openDock()}
          >
            Ouvrir
          </Button>
        </div>
      ) : null}
      <aside
        ref={sheetRef}
        className={[
          dockStyles.sheet,
          expanded ? dockStyles.sheetExpanded : dockStyles.sheetCollapsed,
          stripNudge ? dockStyles.sheetStripNudge : '',
        ]
          .filter(Boolean)
          .join(' ')}
        role="dialog"
        aria-modal={expanded || stripNudge ? true : undefined}
        aria-labelledby={titleId}
        aria-expanded={expanded}
        data-wp-parcours-strip-nudge={stripNudge ? '' : undefined}
      >
        <div className={setupStyles.stripChrome}>
          <div
            className={[
              dockStyles.strip,
              setupStyles.stripRow,
              stripNudge ? dockStyles.stripNudgePulse : '',
            ]
              .filter(Boolean)
              .join(' ')}
            data-wp-dock-strip=""
            data-tour="parcours-strip"
          >
            <button
              type="button"
              className={setupStyles.stripToggle}
              onClick={() => (expanded ? guide.closeDock() : guide.openDock())}
              aria-label={expanded ? `${headerAria}. Réduire.` : `${headerAria}. Ouvrir.`}
            >
              <span className={setupStyles.headerLead}>
                <span id={titleId} className={dockStyles.stripCopy}>
                  Parcours
                </span>
                {!expanded ? (
                  <span className={setupStyles.headerStepHint}>{headerStepLabel}</span>
                ) : null}
              </span>
              <span className={dockStyles.stripEnd}>
                <span className={setupStyles.progressPct}>{progressLabel}</span>
                <span
                  className={
                    expanded
                      ? `${dockStyles.pillTrack} ${dockStyles.pillTrackWide}`
                      : dockStyles.pillTrack
                  }
                  aria-hidden
                >
                  <span className={dockStyles.pillFill} style={{ width: `${pct}%` }} />
                </span>
                <IconChevronRight
                  size={18}
                  stroke={1.6}
                  aria-hidden
                  className={
                    expanded
                      ? `${dockStyles.stripChevron} ${dockStyles.stripChevronExpanded}`
                      : dockStyles.stripChevron
                  }
                />
              </span>
            </button>
            <IconButton
              type="button"
              variant="ghost"
              size="sm"
              borderless
              className={setupStyles.stripDismiss}
              aria-label="Masquer"
              title="Masquer"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setDismissConfirmOpen(true);
              }}
            >
              <IconClock size={18} stroke={1.6} aria-hidden />
            </IconButton>
          </div>
          {expanded && groups.length > 0 ? (
            <ProximityScope axis="x" className={setupStyles.stepTabs} aria-label="Étapes">
              {groups.map((g, i) => {
                const done = g.doneCount >= g.rows.length && g.rows.length > 0;
                const active = i === stepIndex;
                const blocked = guide.isPackBlocked(g.entryId);
                const priorsDone =
                  i <= stepIndex ||
                  groups
                    .slice(0, i)
                    .every((pg) => pg.rows.length > 0 && pg.doneCount >= pg.rows.length);
                const canGo = !blocked && priorsDone;
                const className = [
                  setupStyles.stepTab,
                  active ? setupStyles.stepTabActive : '',
                  done ? setupStyles.stepTabDone : '',
                  canGo ? setupStyles.stepTabNav : '',
                ]
                  .filter(Boolean)
                  .join(' ');
                const label = `${i + 1}. ${g.title}`;
                if (canGo) {
                  return (
                    <button
                      key={g.entryId}
                      type="button"
                      data-wp-proximity-item=""
                      className={className}
                      aria-current={active ? 'step' : undefined}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        goToStep(i);
                      }}
                    >
                      {label}
                    </button>
                  );
                }
                return (
                  <span
                    key={g.entryId}
                    data-wp-proximity-item=""
                    className={className}
                    aria-current={active ? 'step' : undefined}
                  >
                    {label}
                  </span>
                );
              })}
            </ProximityScope>
          ) : null}
        </div>

        <div className={dockStyles.expandSlot} aria-hidden={!expanded}>
          <div className={dockStyles.expandInner} {...(!expanded ? { inert: true } : {})}>
            <ScrollArea
              id="onboarding-dock-drawer"
              className={dockStyles.body}
              fade="y"
              maxHeight="100%"
              data-tour="parcours-dock"
            >
              <StepTravel index={stepIndex}>
              {guide.loading && guide.checklistRows.length === 0 ? (
                <Text size="sm" c="dimmed">
                  Chargement…
                </Text>
              ) : currentGroup ? (
                <section className={setupStyles.stepBlock}>
                  {guide.isPackBlocked(currentGroup.entryId) ? (
                    <div className={dockStyles.blockerLine}>
                      <Text size="sm">
                        {guide.blockedReason(currentGroup.entryId)
                          ? `Commencez par ${guide.blockedReason(currentGroup.entryId)}.`
                          : 'Étapes précédentes requises.'}
                      </Text>
                    </div>
                  ) : (
                    <ul className={dockStyles.list}>
                      {currentGroup.rows.map((row) => {
                        const interactive = row.interactive;
                        return (
                          <li
                            key={row.flow.id}
                            className={
                              row.done
                                ? dockStyles.rowDone
                                : interactive
                                  ? dockStyles.row
                                  : `${dockStyles.row} ${dockStyles.rowDisabled}`
                            }
                          >
                            <button
                              type="button"
                              className={dockStyles.rowMain}
                              tabIndex={expanded && interactive ? undefined : -1}
                              disabled={!interactive}
                              aria-disabled={!interactive}
                              onClick={() => guide.activateChecklistFlow(row.flow.id)}
                            >
                              <span
                                className={row.done ? dockStyles.markDone : dockStyles.markPending}
                                aria-hidden
                              >
                                {row.done ? <IconCheck size={14} stroke={2} /> : null}
                              </span>
                              <span className={dockStyles.rowText}>
                                <span className={dockStyles.rowTitle}>{row.title}</span>
                              </span>
                            </button>
                            {row.skippable && interactive ? (
                              <button
                                type="button"
                                className={dockStyles.skip}
                                tabIndex={expanded ? undefined : -1}
                                aria-label={`Ignorer : ${row.title}`}
                                onClick={() => guide.skipChecklistFlow(row.flow.id)}
                              >
                                <IconX size={14} stroke={1.7} />
                              </button>
                            ) : null}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </section>
              ) : (
                <Text size="sm" c="dimmed">
                  Aucune étape pour ce profil.
                </Text>
              )}
              </StepTravel>
            </ScrollArea>
            <div className={dockStyles.footer}>
              <div className={`${dockStyles.footerInner} ${setupStyles.footerRow}`}>
              {stepIndex > 0 ? (
                <Button
                  type="button"
                  variant="soft"
                  tabIndex={expanded ? undefined : -1}
                  onClick={() => goToStep(stepIndex - 1)}
                >
                  Retour
                </Button>
              ) : (
                <span />
              )}
              <div className={setupStyles.footerEnd}>
                {onLastStep ? (
                  <Button
                    type="button"
                    variant="primary"
                    tabIndex={expanded ? undefined : -1}
                    onClick={guide.closeDock}
                  >
                    Fermer
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="primary"
                    tabIndex={expanded ? undefined : -1}
                    disabled={(() => {
                      const next = groups[stepIndex + 1];
                      return (
                        !next ||
                        guide.isPackBlocked(next.entryId) ||
                        !currentPackToursDone
                      );
                    })()}
                    title={
                      currentPackToursDone
                        ? undefined
                        : 'Terminez ou ignorez les tours de cette étape avant de continuer.'
                    }
                    onClick={() => {
                      const next = groups[stepIndex + 1];
                      if (!next || guide.isPackBlocked(next.entryId) || !currentPackToursDone) {
                        return;
                      }
                      guide.selectPack(next.entryId);
                    }}
                  >
                    Continuer
                  </Button>
                )}
              </div>
              </div>
            </div>
          </div>
        </div>
      </aside>
    </div>
  );

  return typeof document !== 'undefined' ? (
    <>
      {createPortal(sheet, document.body)}
      <Modal
        opened={dismissConfirmOpen}
        onClose={() => setDismissConfirmOpen(false)}
        title="Masquer ?"
        footer={
          <ConfirmModalFooter
            cancelLabel="Rester"
            confirmLabel="Masquer"
            onCancel={() => setDismissConfirmOpen(false)}
            onConfirm={() => {
              setDismissConfirmOpen(false);
              guide.dismissCompanion();
            }}
          />
        }
      >
        <Text size="sm">
          Le parcours restera accessible. Vous pourrez le reprendre depuis Paramètres.
        </Text>
      </Modal>
    </>
  ) : null;
}
